import type { MarginProperties, PaddingProperties } from '@iheartjane/reefer';
import { Box, Flex, Skeleton } from '@iheartjane/reefer';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import styles from './specialDetailLayout.module.css';

type SpecialDetailProps = PropsWithChildren<
  MarginProperties & PaddingProperties
>;

const SpecialDetailContent = ({ children, ...spacing }: SpecialDetailProps) => (
  <Box width="100%" {...spacing}>
    {children}
  </Box>
);

type SpecialDetailBannerProps = SpecialDetailProps & {
  top?: string | number;
};

const SpecialDetailBanner = ({
  children,
  top = 0,
  ...spacing
}: SpecialDetailBannerProps) => (
  <Box className={styles['specialDetailBanner']} top={top} {...spacing}>
    {children}
  </Box>
);

interface SpecialDetailSummaryProps extends PropsWithChildren<
  MarginProperties & PaddingProperties
> {
  headerHeight?: number;
}

const SpecialDetailSummaryWithAnchor = ({
  children,
  headerHeight = 0,
  ...spacing
}: SpecialDetailSummaryProps) => {
  return (
    <div
      className={styles['specialDetailSummary']}
      style={{ '--header-height': `${headerHeight + 48}px` }}
    >
      <Box height="100%" {...spacing}>
        {children}
        <Box
          id="bottom-of-summary-anchor"
          height="1px"
          width="1px"
          position="absolute"
          bottom={-48}
        />
      </Box>
    </div>
  );
};

/** Props for the SpecialDetailLayout compound component */
interface BaseLayoutProps extends SpecialDetailSummaryProps {
  /** Whether the layout is in a loading state */
  isLoading: boolean;
}

const BaseLayoutWithObserver = ({
  children,
  isLoading,
  ...spacing
}: BaseLayoutProps) => {
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window) ||
      !('IntersectionObserverEntry' in window) ||
      !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
    ) {
      return;
    }

    const parentDocument =
      document.getElementById('shadow-host')?.shadowRoot || document;

    const observer = new IntersectionObserver((entries) => {
      const wrapperElement = parentDocument.querySelector(
        '#special-detail-layout'
      );

      if (!wrapperElement) {
        return;
      }

      if (entries[0].boundingClientRect.y < -1) {
        wrapperElement.classList.add('summary-not-at-top');
      } else {
        wrapperElement.classList.remove('summary-not-at-top');
      }
    });

    const targetElement = parentDocument.querySelector(
      '#bottom-of-summary-anchor'
    );
    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => observer.disconnect();
  }, [isLoading]);

  if (isLoading) {
    return (
      <Box className={styles['loadingSkeleton']} {...spacing}>
        <div className={styles['loadingSkeletonSummary']}>
          <Skeleton.Bone height="100%" />
        </div>
        <Flex
          className={styles['loadingSkeletonContent']}
          flexDirection="column"
          gap={24}
        >
          {/* Mobile: search icon, filters pill, sort pill */}
          <Flex
            className={styles['filterSkeletonMobile']}
            gap={16}
            width="100%"
            justifyContent="space-evenly"
          >
            <Skeleton.Bone height="48px" width="20%" />
            <Skeleton.Bone height="48px" width="35%" />
            <Skeleton.Bone height="48px" width="35%" />
          </Flex>

          {/* Desktop: icon buttons + filter pills left, sort pills right */}
          <Flex
            className={styles['filterSkeletonDesktop']}
            justifyContent="space-between"
            width="100%"
          >
            <Flex>
              <Skeleton.Bone height="48px" width="48px" mr={16} />
              <Skeleton.Bone height="48px" width="150px" />
            </Flex>
            <Flex justifyContent="flex-end">
              <Skeleton.Bone height="48px" width="150px" />
            </Flex>
          </Flex>
          <div className={styles['productGridSkeleton']}>
            {Array.from({ length: 12 }, (_, i) => (
              <Skeleton.Bone height={375} key={i} />
            ))}
          </div>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      id="special-detail-layout"
      className={styles['baseLayout']}
      {...spacing}
    >
      {children}
    </Box>
  );
};

export type { BaseLayoutProps as SpecialDetailLayoutProps };

export const SpecialDetailLayout = Object.assign(BaseLayoutWithObserver, {
  Banner: SpecialDetailBanner,
  Content: SpecialDetailContent,
  Summary: SpecialDetailSummaryWithAnchor,
});
